import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ShieldAlert, Lock, Unlock, RefreshCw, ShieldCheck, LogIn } from "lucide-react";
import { SessionsCard } from "./SessionsCard";
import { TeamAccessCard } from "./TeamAccessCard";

const fmt = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export function SecurityTab() {
  const [data, setData] = useState(null);
  const [logins, setLogins] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  const loadLogins = useCallback((p) => {
    api.get(`/admin/security/logins?skip=${p * PAGE_SIZE}&limit=${PAGE_SIZE}`)
      .then((r) => { setLogins(r.data.logins); setTotal(r.data.total || 0); })
      .catch(() => { setLogins([]); setTotal(0); });
  }, []);

  const load = useCallback(() => {
    api.get("/admin/security/attempts").then((r) => setData(r.data)).catch(() => setData({ attempts: [], locked_count: 0, max_attempts: 5, lockout_minutes: 15 }));
    loadLogins(page);
  }, [loadLogins, page]);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const unlock = async (identifier) => {
    try {
      await api.post("/admin/security/unlock", { identifier });
      toast.success("Accesso sbloccato");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!data) return <Skeleton className="h-64 bg-white" />;

  return (
    <div className="space-y-6" data-testid="security-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-6 bg-white border-[#DCE7F5]" data-testid="security-locked-count">
          <div className="flex items-center gap-2 text-[#5A6B85] mb-3">
            <Lock className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-wide">Account bloccati ora</span>
          </div>
          <div className="font-display font-bold text-3xl tracking-tight text-[#0F1E33]">{data.locked_count}</div>
        </Card>
        <Card className="p-6 bg-white border-[#DCE7F5]">
          <div className="flex items-center gap-2 text-[#5A6B85] mb-3">
            <ShieldAlert className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-wide">Soglia blocco</span>
          </div>
          <div className="font-display font-bold text-3xl tracking-tight text-[#0F1E33]">{data.max_attempts} <span className="text-base text-[#94A3B8] font-normal">tentativi</span></div>
        </Card>
        <Card className="p-6 bg-white border-[#DCE7F5]">
          <div className="flex items-center gap-2 text-[#5A6B85] mb-3">
            <ShieldCheck className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-wide">Durata blocco</span>
          </div>
          <div className="font-display font-bold text-3xl tracking-tight text-[#0F1E33]">{data.lockout_minutes} <span className="text-base text-[#94A3B8] font-normal">minuti</span></div>
        </Card>
      </div>

      <SessionsCard />

      <TeamAccessCard />

      <Card className="bg-white border-[#DCE7F5] overflow-hidden">
        <div className="p-5 border-b border-[#DCE7F5] flex items-center justify-between">
          <span className="font-display font-semibold text-[#0F1E33] flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} /> Tentativi di accesso falliti
          </span>
          <Button variant="outline" size="sm" onClick={load} className="border-[#DCE7F5] bg-transparent hover:bg-[#EEF4FC] text-[#0F1E33]" data-testid="security-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Aggiorna
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                <TableHead className="text-[#5A6B85]">Email</TableHead>
                <TableHead className="text-[#5A6B85]">IP</TableHead>
                <TableHead className="text-[#5A6B85] text-center">Tentativi</TableHead>
                <TableHead className="text-[#5A6B85]">Ultimo tentativo</TableHead>
                <TableHead className="text-[#5A6B85] text-center">Stato</TableHead>
                <TableHead className="text-[#5A6B85] text-right">Azione</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.attempts.map((a) => (
                <TableRow key={a.identifier} className="border-[#DCE7F5]" data-testid={`attempt-row-${a.identifier}`}>
                  <TableCell className="font-medium text-[#0F1E33]">{a.email}</TableCell>
                  <TableCell className="text-[#5A6B85]">{a.ip}</TableCell>
                  <TableCell className="text-center text-[#5A6B85]">{a.count}</TableCell>
                  <TableCell className="text-[#5A6B85] text-sm">{fmt(a.last_attempt)}</TableCell>
                  <TableCell className="text-center">
                    {a.locked ? (
                      <Badge className="bg-red-50 text-red-600 border-red-200"><Lock className="w-3 h-3 mr-1" strokeWidth={2} /> Bloccato</Badge>
                    ) : (
                      <span className="text-xs text-[#94A3B8]">Attivo</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {a.locked && (
                      <Button size="sm" variant="ghost" className="h-8 text-[#0B6BCB] hover:bg-[#EEF4FC]" onClick={() => unlock(a.identifier)} data-testid={`unlock-btn-${a.identifier}`}>
                        <Unlock className="w-4 h-4 mr-1" strokeWidth={1.5} /> Sblocca
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {data.attempts.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-[#5A6B85] py-8">Nessun tentativo di accesso fallito registrato</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Card className="bg-white border-[#DCE7F5] overflow-hidden">
        <div className="p-5 border-b border-[#DCE7F5] flex items-center justify-between">
          <span className="font-display font-semibold text-[#0F1E33] flex items-center gap-2">
            <LogIn className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} /> Registro accessi riusciti
          </span>
          <span className="text-xs text-[#5A6B85]" data-testid="logins-total">{total} accessi</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                <TableHead className="text-[#5A6B85]">Utente</TableHead>
                <TableHead className="text-[#5A6B85]">Ruolo</TableHead>
                <TableHead className="text-[#5A6B85]">IP</TableHead>
                <TableHead className="text-[#5A6B85]">Data / ora</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logins.map((l) => (
                <TableRow key={l.id} className="border-[#DCE7F5]" data-testid={`login-row-${l.id}`}>
                  <TableCell>
                    <div className="font-medium text-[#0F1E33]">{l.full_name}</div>
                    <div className="text-xs text-[#94A3B8]">{l.email}</div>
                  </TableCell>
                  <TableCell><Badge className="bg-[#EEF4FC] text-[#0B6BCB] border-[#DCE7F5] capitalize">{l.role}</Badge></TableCell>
                  <TableCell className="text-[#5A6B85]">{l.ip}</TableCell>
                  <TableCell className="text-[#5A6B85] text-sm">{fmt(l.at)}</TableCell>
                </TableRow>
              ))}
              {logins.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-[#5A6B85] py-8">Nessun accesso registrato</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {total > PAGE_SIZE && (
          <div className="p-4 border-t border-[#DCE7F5] flex items-center justify-between" data-testid="logins-pagination">
            <span className="text-xs text-[#5A6B85]">
              Pagina {page + 1} di {Math.ceil(total / PAGE_SIZE)}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="border-[#DCE7F5] bg-transparent hover:bg-[#EEF4FC] text-[#0F1E33] disabled:opacity-40" data-testid="logins-prev-btn">
                Precedente
              </Button>
              <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)} className="border-[#DCE7F5] bg-transparent hover:bg-[#EEF4FC] text-[#0F1E33] disabled:opacity-40" data-testid="logins-next-btn">
                Successiva
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
